import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Chip,
  Alert, TextField, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, Select, MenuItem, FormControl, InputLabel,
  Tabs, Tab, Autocomplete, LinearProgress, IconButton
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import {
  getOportunidadesComerciales, crearOportunidadComercial,
  actualizarOportunidadComercial, borrarOportunidadComercial,
  getVisitasComerciales, crearVisitaComercial,
  actualizarVisitaComercial, borrarVisitaComercial,
  getClientes, getMotivosCierre, cerrarOportunidad, reabrirOportunidad, getMisPermisos
} from '../api';
import OfertasComerciales from '../components/OfertasComerciales';
// ── CONSTANTES ────────────────────────────────────────────
const FASES = [
  { key: 'lead', label: 'Lead', color: '#94a3b8' },
  { key: 'contactado', label: 'Contactado', color: '#0ea5e9' },
  { key: 'propuesta', label: 'Propuesta', color: '#6366f1' },
  { key: 'negociacion', label: 'Negociación', color: '#f59e0b' },
  { key: 'ganada', label: 'Ganada', color: '#22c55e' },
  { key: 'perdida', label: 'Perdida', color: '#ef4444' },
];

const TIPOS_OP = [
  { key: 'grua', label: '🏗 Grúa' },
  { key: 'repuestos', label: '🔩 Repuestos' },
  { key: 'servicio', label: '🔧 Servicio' },
  { key: 'contrato', label: '📄 Contrato mantenimiento' },
];

const TIPOS_VISITA = [
  { key: 'presencial', label: '🤝 Presencial' },
  { key: 'llamada', label: '📞 Llamada' },
  { key: 'videollamada', label: '💻 Videollamada' },
];

const faseInfo = (key: string) => FASES.find(f => f.key === key) || FASES[0];

export default function Comercial() {
  const [tab, setTab] = useState(0);
  const [oportunidades, setOportunidades] = useState<any[]>([]);
  const [visitas, setVisitas] = useState<any[]>([]);
  const [clientes, setClientes] = useState<any[]>([]);

  // Diálogos
  const [opDialog, setOpDialog] = useState<any>(null);
  const [visitaDialog, setVisitaDialog] = useState<any>(null);

  // Cierre de oportunidad
  const [cierreDialog, setCierreDialog] = useState<any>(null);
  const [motivos, setMotivos] = useState<any>({ ganada: [], perdida: [] });
  const [cierre, setCierre] = useState({ resultado: 'ganada', motivo: '', detalle: '' });
  const [cerrando, setCerrando] = useState(false);
  const [misPermisos, setMisPermisos] = useState<string[]>([]);
  const usuarioActual = JSON.parse(localStorage.getItem('user') || '{}');
  const puedeOfertar = usuarioActual.rol === 'admin' || misPermisos.includes('ofertas_comerciales');

  useEffect(() => {
    if (usuarioActual.rol !== 'admin') {
      getMisPermisos().then(setMisPermisos).catch(() => setMisPermisos([]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cargar = () => {
    getOportunidadesComerciales().then(setOportunidades).catch(() => {});
    getVisitasComerciales().then(setVisitas).catch(() => {});
    getClientes().then(setClientes).catch(() => {});
  };

  useEffect(() => {
    cargar();
    getMotivosCierre().then(setMotivos).catch(() => {});
  }, []);

  // ── CIERRE DE OPORTUNIDAD ───────────────────────────────
  const abrirCierre = (o: any, resultado: 'ganada' | 'perdida') => {
    setCierre({ resultado, motivo: '', detalle: '' });
    setCierreDialog(o);
  };

  const confirmarCierre = async () => {
    setCerrando(true);
    try {
      const res = await cerrarOportunidad(cierreDialog.id, cierre);
      setCierreDialog(null);
      cargar();
      if (res.resultado === 'ganada') {
        alert(`✅ Oportunidad ganada: ${res.oportunidad.titulo}\n\nRecuerda dar de alta la máquina como proyecto desde el módulo Máquinas.`);
      } else {
        alert('Oportunidad cerrada como perdida. El motivo queda registrado.');
      }
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Error al cerrar la oportunidad');
    } finally {
      setCerrando(false);
    }
  };

  const handleReabrir = async (o: any) => {
    if (!window.confirm(`¿Reabrir "${o.titulo}"? Volverá al embudo en fase de negociación.`)) return;
    try {
      await reabrirOportunidad(o.id);
      cargar();
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Error al reabrir');
    }
  };

  // ── OPORTUNIDADES ───────────────────────────────────────
  const nuevaOp = () => setOpDialog({
    titulo: '', cliente_id: null, cliente_nombre: '', tipo: 'grua',
    valor_estimado: 0, probabilidad: 50, fase: 'lead',
    fecha_cierre_prevista: '', notas: ''
  });

  const guardarOp = async () => {
    try {
      if (opDialog.id) {
        await actualizarOportunidadComercial(opDialog.id, opDialog);
      } else {
        await crearOportunidadComercial(opDialog);
      }
      setOpDialog(null);
      cargar();
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Error al guardar la oportunidad');
    }
  };

  const eliminarOp = async (id: number) => {
    if (!window.confirm('¿Eliminar esta oportunidad?')) return;
    try {
      await borrarOportunidadComercial(id);
      cargar();
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Error al eliminar');
    }
  };

  // ── VISITAS ─────────────────────────────────────────────
  const nuevaVisita = () => setVisitaDialog({
    cliente_id: null, cliente_nombre: '', tipo: 'presencial',
    fecha: new Date().toISOString().slice(0, 10), estado: 'programada',
    notas: '', proxima_accion: '', fecha_seguimiento: '', oportunidad_id: null
  });

  const guardarVisita = async () => {
    try {
      if (visitaDialog.id) {
        await actualizarVisitaComercial(visitaDialog.id, visitaDialog);
      } else {
        await crearVisitaComercial(visitaDialog);
      }
      setVisitaDialog(null);
      cargar();
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Error al guardar la visita');
    }
  };

  const eliminarVisita = async (id: number) => {
    if (!window.confirm('¿Eliminar esta visita?')) return;
    try {
      await borrarVisitaComercial(id);
      cargar();
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Error al eliminar');
    }
  };

  // Resumen del pipeline (valor ponderado por probabilidad)
  const valorPipeline = oportunidades
    .filter(o => !['ganada', 'perdida'].includes(o.fase))
    .reduce((sum, o) => sum + (o.valor_estimado * o.probabilidad / 100), 0);
  const valorGanado = oportunidades
    .filter(o => o.fase === 'ganada')
    .reduce((sum, o) => sum + o.valor_estimado, 0);

  return (
    <Box sx={{ width: '100%', overflowX: 'hidden' }}>
      <Typography variant="h4" sx={{ fontWeight: 600, mb: 3, fontSize: { xs: '1.5rem', md: '2rem' } }}>
        📊 Comercial
      </Typography>

      {/* Resumen */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card sx={{ borderRadius: 3, borderLeft: '4px solid #6366f1' }}>
            <CardContent>
              <Typography variant="caption" color="text.secondary">Pipeline ponderado</Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>{valorPipeline.toLocaleString('es-ES', { maximumFractionDigits: 0 })} €</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ borderRadius: 3, borderLeft: '4px solid #22c55e' }}>
            <CardContent>
              <Typography variant="caption" color="text.secondary">Ganado</Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>{valorGanado.toLocaleString('es-ES', { maximumFractionDigits: 0 })} €</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ borderRadius: 3, borderLeft: '4px solid #f59e0b' }}>
            <CardContent>
              <Typography variant="caption" color="text.secondary">Oportunidades abiertas</Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {oportunidades.filter(o => !['ganada', 'perdida'].includes(o.fase)).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}
        variant="scrollable" scrollButtons="auto">
        <Tab label="💼 Oportunidades" />
        <Tab label="🤝 Visitas" />
        {puedeOfertar && <Tab label="📊 Ofertas" />}
      </Tabs>

      {tab === 2 && puedeOfertar && <OfertasComerciales oportunidades={oportunidades} />}

      {/* TAB OPORTUNIDADES */}
      {tab === 0 && (
        <Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={nuevaOp}
            sx={{ mb: 2, bgcolor: '#4f46e5' }}>
            Nueva oportunidad
          </Button>
          <Grid container spacing={2}>
            {oportunidades.map(o => {
              const fi = faseInfo(o.fase);
              return (
                <Grid item xs={12} sm={6} lg={4} key={o.id}>
                  <Card sx={{ borderRadius: 3, borderLeft: `4px solid ${fi.color}` }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Typography sx={{ fontWeight: 600, fontSize: 15 }}>{o.titulo}</Typography>
                        <Chip label={fi.label} size="small" sx={{ bgcolor: fi.color, color: 'white', fontWeight: 600 }} />
                      </Box>
                      <Typography variant="caption" display="block">👤 {o.cliente_nombre || '—'}</Typography>
                      <Typography variant="caption" display="block">
                        {TIPOS_OP.find(t => t.key === o.tipo)?.label || o.tipo}
                      </Typography>
                      <Typography variant="caption" display="block">💰 {o.valor_estimado?.toLocaleString('es-ES')} €</Typography>
                      <Typography variant="caption" display="block">📅 Cierre: {o.fecha_cierre_prevista || '—'}</Typography>
                      <Typography variant="caption" display="block">👷 {o.comercial_nombre} {o.comercial_apellidos}</Typography>
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="caption">Probabilidad: {o.probabilidad}%</Typography>
                        <LinearProgress variant="determinate" value={o.probabilidad}
                          sx={{ height: 6, borderRadius: 3, mt: 0.5 }} />
                      </Box>
                      {/* Motivo del cierre si está cerrada */}
                      {['ganada', 'perdida'].includes(o.fase) && o.motivo_cierre && (
                        <Alert severity={o.fase === 'ganada' ? 'success' : 'error'}
                          sx={{ mt: 1.5, py: 0 }}>
                          <Typography variant="caption" display="block">
                            <b>{o.fase === 'ganada' ? 'Ganada' : 'Perdida'}:</b> {o.motivo_cierre}
                            {o.fecha_cierre_real ? ` · ${o.fecha_cierre_real}` : ''}
                          </Typography>
                          {o.detalle_cierre && (
                            <Typography variant="caption" display="block">{o.detalle_cierre}</Typography>
                          )}
                        </Alert>
                      )}

                      {/* Acciones de cierre */}
                      {!['ganada', 'perdida'].includes(o.fase) && (
                        <Box sx={{ display: 'flex', gap: 0.5, mt: 1.5 }}>
                          <Button size="small" variant="contained"
                            onClick={() => abrirCierre(o, 'ganada')}
                            sx={{ bgcolor: '#22c55e', flex: 1, '&:hover': { bgcolor: '#16a34a' } }}>
                            ✅ Ganada
                          </Button>
                          <Button size="small" variant="outlined" color="error"
                            onClick={() => abrirCierre(o, 'perdida')} sx={{ flex: 1 }}>
                            ❌ Perdida
                          </Button>
                        </Box>
                      )}

                      <Box sx={{ display: 'flex', gap: 0.5, mt: 1, alignItems: 'center' }}>
                        <Button size="small" startIcon={<EditIcon fontSize="small" />}
                          onClick={() => setOpDialog({ ...o })}>Editar</Button>
                        {['ganada', 'perdida'].includes(o.fase) && (
                          <Button size="small" onClick={() => handleReabrir(o)}>↩️ Reabrir</Button>
                        )}
                        <Box sx={{ flex: 1 }} />
                        <IconButton size="small" color="error" onClick={() => eliminarOp(o.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
            {oportunidades.length === 0 && (
              <Grid item xs={12}><Alert severity="info">No hay oportunidades registradas todavía.</Alert></Grid>
            )}
          </Grid>
        </Box>
      )}

      {/* TAB VISITAS */}
      {tab === 1 && (
        <Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={nuevaVisita}
            sx={{ mb: 2, bgcolor: '#4f46e5' }}>
            Nueva visita
          </Button>
          <Grid container spacing={2}>
            {visitas.map(v => (
              <Grid item xs={12} sm={6} lg={4} key={v.id}>
                <Card sx={{ borderRadius: 3, borderLeft: `4px solid ${v.estado === 'realizada' ? '#22c55e' : '#0ea5e9'}` }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Typography sx={{ fontWeight: 600 }}>
                        {TIPOS_VISITA.find(t => t.key === v.tipo)?.label || v.tipo}
                      </Typography>
                      <Chip label={v.estado} size="small"
                        color={v.estado === 'realizada' ? 'success' : 'info'} />
                    </Box>
                    <Typography variant="caption" display="block">👤 {v.cliente_nombre || '—'}</Typography>
                    <Typography variant="caption" display="block">📅 {v.fecha}</Typography>
                    {v.oportunidad_titulo && (
                      <Typography variant="caption" display="block">🔗 {v.oportunidad_titulo}</Typography>
                    )}
                    {v.notas && <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>📝 {v.notas}</Typography>}
                    {v.proxima_accion && (
                      <Alert severity="warning" sx={{ mt: 1, py: 0 }}>
                        <Typography variant="caption">
                          ⏭ {v.proxima_accion} {v.fecha_seguimiento ? `(${v.fecha_seguimiento})` : ''}
                        </Typography>
                      </Alert>
                    )}
                    <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>👷 {v.comercial_nombre} {v.comercial_apellidos}</Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, mt: 1.5 }}>
                      <Button size="small" startIcon={<EditIcon fontSize="small" />}
                        onClick={() => setVisitaDialog({ ...v })}>Editar</Button>
                      <IconButton size="small" color="error" onClick={() => eliminarVisita(v.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
            {visitas.length === 0 && (
              <Grid item xs={12}><Alert severity="info">No hay visitas registradas todavía.</Alert></Grid>
            )}
          </Grid>
        </Box>
      )}

      {/* DIALOG OPORTUNIDAD */}
      <Dialog open={!!opDialog} onClose={(e, r) => { if (r !== 'backdropClick' && r !== 'escapeKeyDown') { setOpDialog(null); } }} maxWidth="sm" fullWidth>
        <DialogTitle>{opDialog?.id ? 'Editar oportunidad' : 'Nueva oportunidad'}</DialogTitle>
        <DialogContent>
          {opDialog && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField label="Título *" value={opDialog.titulo}
                onChange={(e) => setOpDialog({ ...opDialog, titulo: e.target.value })} />
              <Autocomplete
                options={clientes}
                getOptionLabel={(c: any) => c.nombre}
                value={clientes.find(c => c.id === opDialog.cliente_id) || null}
                onChange={(_, val) => setOpDialog({ ...opDialog, cliente_id: val?.id || null, cliente_nombre: val?.nombre || '' })}
                renderInput={(params) => <TextField {...params} label="Cliente" />}
              />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>Tipo</InputLabel>
                  <Select value={opDialog.tipo} label="Tipo"
                    onChange={(e) => setOpDialog({ ...opDialog, tipo: e.target.value })}>
                    {TIPOS_OP.map(t => <MenuItem key={t.key} value={t.key}>{t.label}</MenuItem>)}
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel>Fase</InputLabel>
                  <Select value={opDialog.fase} label="Fase"
                    onChange={(e) => setOpDialog({ ...opDialog, fase: e.target.value })}>
                    {FASES.map(f => <MenuItem key={f.key} value={f.key}>{f.label}</MenuItem>)}
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField label="Valor estimado (€)" type="number" fullWidth value={opDialog.valor_estimado}
                  onChange={(e) => setOpDialog({ ...opDialog, valor_estimado: parseFloat(e.target.value) || 0 })} />
                <TextField label="Probabilidad (%)" type="number" fullWidth value={opDialog.probabilidad}
                  onChange={(e) => setOpDialog({ ...opDialog, probabilidad: parseInt(e.target.value) || 0 })} />
              </Box>
              <TextField label="Fecha cierre prevista" type="date" InputLabelProps={{ shrink: true }}
                value={opDialog.fecha_cierre_prevista || ''}
                onChange={(e) => setOpDialog({ ...opDialog, fecha_cierre_prevista: e.target.value })} />
              <TextField label="Notas" multiline rows={2} value={opDialog.notas || ''}
                onChange={(e) => setOpDialog({ ...opDialog, notas: e.target.value })} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpDialog(null)}>Cancelar</Button>
          <Button onClick={guardarOp} variant="contained" sx={{ bgcolor: '#4f46e5' }}
            disabled={!opDialog?.titulo}>Guardar</Button>
        </DialogActions>
      </Dialog>

      {/* DIALOG VISITA */}
      <Dialog open={!!visitaDialog} onClose={(e, r) => { if (r !== 'backdropClick' && r !== 'escapeKeyDown') { setVisitaDialog(null); } }} maxWidth="sm" fullWidth>
        <DialogTitle>{visitaDialog?.id ? 'Editar visita' : 'Nueva visita'}</DialogTitle>
        <DialogContent>
          {visitaDialog && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <Autocomplete
                options={clientes}
                getOptionLabel={(c: any) => c.nombre}
                value={clientes.find(c => c.id === visitaDialog.cliente_id) || null}
                onChange={(_, val) => setVisitaDialog({ ...visitaDialog, cliente_id: val?.id || null, cliente_nombre: val?.nombre || '' })}
                renderInput={(params) => <TextField {...params} label="Cliente" />}
              />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>Tipo</InputLabel>
                  <Select value={visitaDialog.tipo} label="Tipo"
                    onChange={(e) => setVisitaDialog({ ...visitaDialog, tipo: e.target.value })}>
                    {TIPOS_VISITA.map(t => <MenuItem key={t.key} value={t.key}>{t.label}</MenuItem>)}
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel>Estado</InputLabel>
                  <Select value={visitaDialog.estado} label="Estado"
                    onChange={(e) => setVisitaDialog({ ...visitaDialog, estado: e.target.value })}>
                    <MenuItem value="programada">Programada</MenuItem>
                    <MenuItem value="realizada">Realizada</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <TextField label="Fecha de la visita *" type="date" InputLabelProps={{ shrink: true }}
                value={visitaDialog.fecha}
                onChange={(e) => setVisitaDialog({ ...visitaDialog, fecha: e.target.value })} />
              <FormControl fullWidth>
                <InputLabel>Vincular a oportunidad (opcional)</InputLabel>
                <Select value={visitaDialog.oportunidad_id || ''} label="Vincular a oportunidad (opcional)"
                  onChange={(e) => setVisitaDialog({ ...visitaDialog, oportunidad_id: e.target.value || null })}>
                  <MenuItem value="">Sin vincular</MenuItem>
                  {oportunidades.map(o => <MenuItem key={o.id} value={o.id}>{o.titulo}</MenuItem>)}
                </Select>
              </FormControl>
              <TextField label="Notas / resultado" multiline rows={2} value={visitaDialog.notas || ''}
                onChange={(e) => setVisitaDialog({ ...visitaDialog, notas: e.target.value })} />
              <TextField label="Próxima acción" value={visitaDialog.proxima_accion || ''}
                onChange={(e) => setVisitaDialog({ ...visitaDialog, proxima_accion: e.target.value })} />
              <TextField label="Fecha de seguimiento" type="date" InputLabelProps={{ shrink: true }}
                value={visitaDialog.fecha_seguimiento || ''}
                onChange={(e) => setVisitaDialog({ ...visitaDialog, fecha_seguimiento: e.target.value })} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVisitaDialog(null)}>Cancelar</Button>
          <Button onClick={guardarVisita} variant="contained" sx={{ bgcolor: '#4f46e5' }}
            disabled={!visitaDialog?.fecha}>Guardar</Button>
        </DialogActions>
      </Dialog>

      {/* DIALOG CIERRE DE OPORTUNIDAD */}
      <Dialog open={!!cierreDialog} onClose={(e, r) => { if (r !== 'backdropClick' && r !== 'escapeKeyDown') { setCierreDialog(null); } }} maxWidth="sm" fullWidth>
        <DialogTitle>
          {cierre.resultado === 'ganada' ? '✅ Cerrar como GANADA' : '❌ Cerrar como PERDIDA'}
        </DialogTitle>
        <DialogContent>
          {cierreDialog && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                <b>{cierreDialog.titulo}</b> — {cierreDialog.cliente_nombre || 'sin cliente'} ·
                {' '}{cierreDialog.valor_estimado?.toLocaleString('es-ES')} €
              </Typography>

              <Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Motivo * <Typography component="span" variant="caption" color="text.secondary">
                    (elige uno o escribe el tuyo)
                  </Typography>
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1.5 }}>
                  {(cierre.resultado === 'ganada' ? motivos.ganada : motivos.perdida).map((m: string) => (
                    <Chip key={m} label={m} size="small"
                      color={cierre.motivo === m ? 'primary' : 'default'}
                      onClick={() => setCierre({ ...cierre, motivo: m })}
                      sx={{ cursor: 'pointer' }} />
                  ))}
                </Box>
                <TextField fullWidth size="small" label="Motivo"
                  value={cierre.motivo}
                  onChange={(e) => setCierre({ ...cierre, motivo: e.target.value })} />
              </Box>

              <TextField fullWidth multiline rows={3} label="Detalle (opcional)"
                placeholder={cierre.resultado === 'ganada'
                  ? 'Ej: entrega en 8 semanas, financiación a 48 meses...'
                  : 'Ej: se fueron a la competencia por 12.000 € menos y entrega inmediata'}
                value={cierre.detalle}
                onChange={(e) => setCierre({ ...cierre, detalle: e.target.value })} />

            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCierreDialog(null)}>Cancelar</Button>
          <Button variant="contained" onClick={confirmarCierre}
            disabled={cerrando || !cierre.motivo.trim()}
            sx={{ bgcolor: cierre.resultado === 'ganada' ? '#22c55e' : '#ef4444' }}>
            {cerrando ? 'Cerrando...' : (cierre.resultado === 'ganada' ? 'Marcar como ganada' : 'Marcar como perdida')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}